import { useEffect, useState, useMemo, useCallback } from 'react';
import { createRoute, redirect, useNavigate, useSearch } from '@tanstack/react-router';
import { useAuth0 } from '@auth0/auth0-react';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  SortingState,
  flexRender,
} from '@tanstack/react-table';
import { format, subHours, subDays } from 'date-fns';
import { Route as rootRoute } from '../__root';
import { Layout } from '@/client/layouts/layout';
import { useTranscodeJobsStore } from '@/client/store/transcodeJobsStore';
import { useClientsStore } from '@/client/store/clientsStore';
import { useStallDetection } from '@/client/hooks/useStallDetection';
import { usePersistedSearch } from '@/client/hooks/usePersistedSearch';
import { ResetFiltersButton } from '@/client/components/reset-filters-button';
import { columns } from '@/client/components/tables/transcode-jobs-columns';
import type { TranscodeJob, TrackedJob } from '@shared/types';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/client/components/ui/table';
import { Input } from '@/client/components/ui/input';
import { Button } from '@/client/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/client/components/ui/dropdown-menu';
import { Badge } from '@/client/components/ui/badge';
import { RefreshCw, X, ChevronDown, AlertTriangle, ChevronLeft, ChevronRight } from 'lucide-react';
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
} from '@/client/components/ui/pagination';

// Quick range presets
const QUICK_RANGES = [
  { label: '24h', hours: 24 },
  { label: '48h', hours: 48 },
  { label: '72h', hours: 72 },
  { label: '1 week', hours: 168 },
  { label: '1 month', hours: 720 },
  { label: '2 months', hours: 1440 },
  { label: '3 months', hours: 2160 },
] as const;

type QuickRangeLabel = typeof QUICK_RANGES[number]['label'];

type TranscodeJobsSearch = {
  clientID?: string;
  range?: QuickRangeLabel;
};

export const Route = createRoute({
  getParentRoute: () => rootRoute,
  path: 'transcode-jobs',
  validateSearch: (search: Record<string, unknown>): TranscodeJobsSearch => {
    return {
      clientID: typeof search.clientID === 'string' ? search.clientID : undefined,
      range: typeof search.range === 'string' ? search.range as QuickRangeLabel : undefined,
    };
  },
  beforeLoad: async ({ context }) => {
    if (!context.auth.isAuthenticated) {
      throw redirect({
        to: '/',
      });
    }
  },
  component: TranscodeJobsComponent,
});

function getDateRangeFromQuickRange(rangeLabel: QuickRangeLabel | undefined): { startDate: string; endDate: string } {
  const now = new Date();
  const range = QUICK_RANGES.find(r => r.label === rangeLabel) || QUICK_RANGES[0]; // Default to 24h

  const start = range.hours <= 72
    ? subHours(now, range.hours)
    : subDays(now, range.hours / 24);

  return {
    startDate: format(start, 'yyyy-MM-dd'),
    endDate: format(now, 'yyyy-MM-dd'),
  };
}

function TranscodeJobsComponent() {
  const { getAccessTokenSilently, logout } = useAuth0();

  const navigate = useNavigate();
  const { clientID, range = '24h' } = useSearch({ from: '/transcode-jobs' });

  // Persist search params across navigation
  usePersistedSearch('/transcode-jobs', { clientID: undefined, range: '24h' });

  // Store state
  const processing = useTranscodeJobsStore((state) => state.processing);
  const failed = useTranscodeJobsStore((state) => state.failed);
  const success = useTranscodeJobsStore((state) => state.success);
  const successPaginated = useTranscodeJobsStore((state) => state.successPaginated);
  const loading = useTranscodeJobsStore((state) => state.loading);
  const error = useTranscodeJobsStore((state) => state.error);
  const lastMeta = useTranscodeJobsStore((state) => state.lastMeta);
  const fetchJobs = useTranscodeJobsStore((state) => state.fetchJobs);
  const fetchSuccessJobs = useTranscodeJobsStore((state) => state.fetchSuccessJobs);

  const { clients, fetchClients } = useClientsStore();
  const [sorting, setSorting] = useState<SortingState>([{ id: 'createdAt', desc: true }]);
  const [searchFilter, setSearchFilter] = useState('');
  const [activeTab, setActiveTab] = useState<'processing' | 'failed' | 'success'>('processing');

  // Enable stall detection while on this page
  useStallDetection();

  const filteredClient = clientID ? clients.find(c => c.clientID === clientID) : null;

  // Convert Map to array for display
  const processingJobs = useMemo(() => Array.from(processing.values()), [processing]);

  // Count stalled/silent-failure jobs
  const problemJobsCount = useMemo(() => {
    return processingJobs.filter(j => j.stallStatus !== 'normal').length;
  }, [processingJobs]);

  // Get jobs for current tab
  // For success tab, combine WSS live completions with paginated historical data
  const currentJobs: TranscodeJob[] = useMemo(() => {
    switch (activeTab) {
      case 'processing':
        return processingJobs;
      case 'failed':
        return failed;
      case 'success':
        // Combine live WSS completions with paginated API results
        // Deduplicate by job ID (WSS takes priority)
        const wssIds = new Set(success.map(j => j.id));
        const deduped = successPaginated.jobs.filter(j => !wssIds.has(j.id));
        return [...success, ...deduped];
      default:
        return [];
    }
  }, [activeTab, processingJobs, failed, success, successPaginated.jobs]);

  // Apply search filter
  const filteredJobs = useMemo(() => {
    if (!searchFilter) return currentJobs;
    const search = searchFilter.toLowerCase();
    return currentJobs.filter((job) =>
      job.id.toLowerCase().includes(search) ||
      job.jobType?.toLowerCase().includes(search) ||
      job.clientID?.toLowerCase().includes(search) ||
      job.eventID?.toLowerCase().includes(search) ||
      job.assetID?.toLowerCase().includes(search)
    );
  }, [currentJobs, searchFilter]);

  const loadData = useCallback(async () => {
    try {
      const token = await getAccessTokenSilently();
      const { startDate, endDate } = getDateRangeFromQuickRange(range);

      await Promise.all([
        fetchJobs(token, logout, { clientID, startDate, endDate }),
        clients.length === 0 ? fetchClients(token, logout) : Promise.resolve(),
      ]);
      setLastFetchTime(Date.now());
    } catch (err) {
      console.error('Error fetching data:', err);
    }
  }, [getAccessTokenSilently, fetchJobs, fetchClients, clients.length, clientID, range]);

  // Initial load
  useEffect(() => {
    loadData();
  }, [loadData]);

  // Track last fetch time for visibility change cooldown
  const [lastFetchTime, setLastFetchTime] = useState<number>(Date.now());

  // Track what success jobs filter params we've already fetched
  // This prevents infinite loops when the API returns empty results
  const [successFetchedFor, setSuccessFetchedFor] = useState<string | null>(null);

  // Fetch success jobs when switching to success tab or changing date range
  const loadSuccessJobs = useCallback(async (page = 1) => {
    try {
      const token = await getAccessTokenSilently();
      const { startDate, endDate } = getDateRangeFromQuickRange(range);
      await fetchSuccessJobs(token, logout, page, { clientID, startDate, endDate });
    } catch (err) {
      console.error('Error fetching success jobs:', err);
    }
  }, [getAccessTokenSilently, fetchSuccessJobs, range, clientID, logout]);

  // Fetch success jobs on tab switch or when filters change
  useEffect(() => {
    // Create a key representing current filter state
    const filterKey = `${range}:${clientID ?? 'all'}`;
    const alreadyFetched = successFetchedFor === filterKey;

    // Fetch if on success tab AND we haven't fetched for these filters yet
    if (activeTab === 'success' && !successPaginated.loading && !alreadyFetched) {
      setSuccessFetchedFor(filterKey);
      loadSuccessJobs(1);
    }
  }, [activeTab, successPaginated.loading, loadSuccessJobs, range, clientID, successFetchedFor]);

  // Reset the fetched-for tracker when filters change so we re-fetch with new params
  useEffect(() => {
    setSuccessFetchedFor(null);
  }, [range, clientID]);

  const handlePageChange = useCallback((newPage: number) => {
    loadSuccessJobs(newPage);
  }, [loadSuccessJobs]);

  // Refresh on wake from sleep / tab visibility change (with 5-minute cooldown)
  useEffect(() => {
    const VISIBILITY_REFETCH_COOLDOWN = 5 * 60 * 1000; // 5 minutes

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        const timeSinceLastFetch = Date.now() - lastFetchTime;
        if (timeSinceLastFetch >= VISIBILITY_REFETCH_COOLDOWN) {
          loadData();
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [loadData, lastFetchTime]);

  const table = useReactTable({
    data: filteredJobs,
    columns,
    getCoreRowModel: getCoreRowModel(),
    onSortingChange: setSorting,
    getSortedRowModel: getSortedRowModel(),
    state: {
      sorting,
    },
  });

  const handleRefresh = async () => {
    try {
      const token = await getAccessTokenSilently();
      const { startDate, endDate } = getDateRangeFromQuickRange(range);
      await fetchJobs(token, logout, { clientID, startDate, endDate });
    } catch (err) {
      console.error('Error refreshing jobs:', err);
    }
  };

  const clearClientFilter = () => {
    navigate({ to: '/transcode-jobs', search: { range } });
  };

  const handleRangeChange = (newRange: QuickRangeLabel) => {
    navigate({
      to: '/transcode-jobs',
      search: { clientID, range: newRange },
    });
  };

  return (
    <Layout>
      <div>
        <section>
          <div className="flex justify-between items-center mb-4">
            <div className="flex items-center gap-4">
              <h3 className="text-2xl font-semibold">Transcode Jobs</h3>
              {filteredClient && (
                <div className="flex items-center gap-1 px-3 py-1 bg-muted rounded-full text-sm">
                  <span>{filteredClient.name}</span>
                  <button
                    onClick={clearClientFilter}
                    className="ml-1 hover:bg-accent rounded-full p-0.5"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              )}
              {problemJobsCount > 0 && (
                <Badge variant="destructive" className="gap-1">
                  <AlertTriangle className="h-3 w-3" />
                  {problemJobsCount} job{problemJobsCount !== 1 ? 's' : ''} need attention
                </Badge>
              )}
            </div>
            <p className="font-mono text-sm text-muted-foreground">Live data</p>
          </div>

          {loading && <p>Loading transcode jobs...</p>}

          {error && (
            <div className="p-4 bg-red-100 border-l-4 border-red-600 rounded mt-4">
              <p className="m-0 text-red-800 font-medium">Error: {error}</p>
            </div>
          )}

          {!loading && !error && (
            <>
              {/* Controls Row */}
              <div className="flex justify-between items-center gap-4 mb-4">
                {/* Left: Tabs */}
                <div className="flex gap-1 p-1 bg-muted rounded-lg">
                  <Button
                    variant={activeTab === 'processing' ? 'default' : 'ghost'}
                    size="sm"
                    onClick={() => setActiveTab('processing')}
                    className="gap-2"
                  >
                    Processing
                    <Badge variant="secondary" className="ml-1">
                      {processingJobs.length}
                    </Badge>
                  </Button>
                  <Button
                    variant={activeTab === 'failed' ? 'default' : 'ghost'}
                    size="sm"
                    onClick={() => setActiveTab('failed')}
                    className="gap-2"
                  >
                    Failed
                    <Badge variant="secondary" className="ml-1">
                      {failed.length}
                    </Badge>
                  </Button>
                  <Button
                    variant={activeTab === 'success' ? 'default' : 'ghost'}
                    size="sm"
                    onClick={() => setActiveTab('success')}
                    className="gap-2"
                  >
                    Success
                    <Badge variant="secondary" className="ml-1">
                      {successPaginated.totalCount > 0 ? successPaginated.totalCount : success.length}
                    </Badge>
                  </Button>
                </div>

                {/* Right: Filters and Actions */}
                <div className="flex items-center gap-4">
                  {/* Quick Range Dropdown */}
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="outline" className="w-32 justify-between">
                        {range}
                        <ChevronDown className="ml-2 h-4 w-4 opacity-50" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      {QUICK_RANGES.map((r) => (
                        <DropdownMenuItem
                          key={r.label}
                          onClick={() => handleRangeChange(r.label)}
                        >
                          {r.label}
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>

                  <Input
                    placeholder="Filter by ID, type, client..."
                    value={searchFilter}
                    onChange={(e) => setSearchFilter(e.target.value)}
                    className="w-64"
                  />

                  <Button
                    variant="outline"
                    onClick={handleRefresh}
                    disabled={loading}
                  >
                    <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                    Force Refresh
                  </Button>
                  <ResetFiltersButton
                    routePath="/transcode-jobs"
                    defaultValues={{ clientID: undefined, range: '24h' }}
                  />
                </div>
              </div>

              {/* Table */}
              {activeTab === 'success' && successPaginated.loading && (
                <div className="text-center py-8 text-muted-foreground">
                  Loading success jobs...
                </div>
              )}

              {filteredJobs.length === 0 && !(activeTab === 'success' && successPaginated.loading) ? (
                <div className="text-center py-8 text-muted-foreground">
                  No {activeTab} jobs found for the selected time range.
                </div>
              ) : filteredJobs.length > 0 && (
                <>
                  <div className="rounded-md border">
                    <Table>
                      <TableHeader>
                        {table.getHeaderGroups().map((headerGroup) => (
                          <TableRow key={headerGroup.id}>
                            {headerGroup.headers.map((header) => (
                              <TableHead key={header.id}>
                                {header.isPlaceholder
                                  ? null
                                  : flexRender(
                                      header.column.columnDef.header,
                                      header.getContext()
                                    )}
                              </TableHead>
                            ))}
                          </TableRow>
                        ))}
                      </TableHeader>
                      <TableBody>
                        {table.getRowModel().rows.map((row) => (
                          <TableRow key={row.id}>
                            {row.getVisibleCells().map((cell) => (
                              <TableCell key={cell.id}>
                                {flexRender(
                                  cell.column.columnDef.cell,
                                  cell.getContext()
                                )}
                              </TableCell>
                            ))}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                  <div className="mt-2 flex justify-between items-center text-sm text-muted-foreground">
                    <span>
                      {activeTab === 'success' && successPaginated.totalCount > 0
                        ? `Showing page ${successPaginated.page} of ${successPaginated.totalPages} (${successPaginated.totalCount} total)`
                        : `Showing ${filteredJobs.length} ${activeTab} job${filteredJobs.length !== 1 ? 's' : ''}`
                      }
                    </span>
                    {lastMeta && (
                      <span>
                        Range: {new Date(lastMeta.startDate).toLocaleDateString()} - {new Date(lastMeta.endDate).toLocaleDateString()}
                      </span>
                    )}
                  </div>

                  {/* Pagination controls for success tab */}
                  {activeTab === 'success' && successPaginated.totalPages > 1 && (
                    <div className="mt-4 flex justify-center">
                      <Pagination>
                        <PaginationContent>
                          <PaginationItem>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handlePageChange(successPaginated.page - 1)}
                              disabled={successPaginated.page <= 1 || successPaginated.loading}
                              className="gap-1"
                            >
                              <ChevronLeft className="h-4 w-4" />
                              Previous
                            </Button>
                          </PaginationItem>

                          {/* Page numbers */}
                          {Array.from({ length: Math.min(5, successPaginated.totalPages) }, (_, i) => {
                            let pageNum: number;
                            if (successPaginated.totalPages <= 5) {
                              pageNum = i + 1;
                            } else if (successPaginated.page <= 3) {
                              pageNum = i + 1;
                            } else if (successPaginated.page >= successPaginated.totalPages - 2) {
                              pageNum = successPaginated.totalPages - 4 + i;
                            } else {
                              pageNum = successPaginated.page - 2 + i;
                            }
                            return (
                              <PaginationItem key={pageNum}>
                                <PaginationLink
                                  onClick={() => handlePageChange(pageNum)}
                                  isActive={pageNum === successPaginated.page}
                                  className="cursor-pointer"
                                >
                                  {pageNum}
                                </PaginationLink>
                              </PaginationItem>
                            );
                          })}

                          <PaginationItem>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handlePageChange(successPaginated.page + 1)}
                              disabled={successPaginated.page >= successPaginated.totalPages || successPaginated.loading}
                              className="gap-1"
                            >
                              Next
                              <ChevronRight className="h-4 w-4" />
                            </Button>
                          </PaginationItem>
                        </PaginationContent>
                      </Pagination>
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </section>
      </div>
    </Layout>
  );
}
